import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Pagination
} from "@mui/material";
import ArrowDropUpOutlinedIcon from "@mui/icons-material/ArrowDropUpOutlined";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { PageSizeSelector } from "./pageSizeSelector";
import PaginationGoToPageTextbox from "./paginationGoToPageTextbox";
import { IGridcolumn } from "./interface/column.interface";

export interface IProps {
  columns: IGridcolumn[];
  data: any[];
  selection?: boolean;
  selectionChanged?: (id: string[]) => void;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC" | "";
  sortChanged?: (column: string, direction: string) => void;
  pageChanged?: (page: number) => void;
  pageSizeChanged?: (size: number) => void;
  totalPages?: number;
  currentPage?: number;
}

// ✅ Memoized Checkbox Cell
const CheckboxCell = React.memo(
  ({
    checked,
    onChange
  }: {
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    return (
      <TableCell>
        <Checkbox color="primary" checked={checked} onChange={onChange} />
      </TableCell>
    );
  }
);

// ✅ Memoized Row
const TableDataRow = React.memo(
  ({
    row,
    columns,
    isSelected,
    selection,
    rowKey,
    onCheckboxChange
  }: {
    row: any;
    columns: IGridcolumn[];
    isSelected: boolean;
    selection: boolean;
    rowKey: string;
    onCheckboxChange: (id: string, checked: boolean) => void;
  }) => {
    const handleCheckChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onCheckboxChange(row[rowKey], e.target.checked);
      },
      [row, rowKey, onCheckboxChange]
    );

    return (
      <TableRow>
        {selection && (
          <CheckboxCell checked={isSelected} onChange={handleCheckChange} />
        )}
        {columns.map((col, colIndex) =>
          col.redner ? (
            <TableCell
              key={col.fieldName ?? col.caption ?? colIndex}
              style={{
                textAlign: col.caption === "ACTION" ? "center" : "left"
              }}
            >
              <col.redner
                {...{
                  ...row,
                  status:
                    row.status === "TIMEOUT_STOPPED"
                      ? "TIMEOUT"
                      : row.status
                }}
              />
            </TableCell>
          ) : (
            <TableCell
              key={col.fieldName ?? col.caption ?? colIndex}
              style={{
                textAlign: col.caption === "ACTION" ? "center" : "left"
              }}
            >
              {row[col.fieldName]}
            </TableCell>
          )
        )}
      </TableRow>
    );
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.row[prev.rowKey] === next.row[next.rowKey]
);

export const TestResultStatusGrid = ({
  columns = [],
  data = [],
  selection = false,
  selectionChanged,
  sortChanged,
  sortBy = "",
  sortDirection = "ASC",
  totalPages = 0,
  pageChanged,
  pageSizeChanged,
  currentPage = 1
}: IProps) => {
  const [order, setOrder] = useState<string>("");
  const [sort, setSort] = useState<string>("");
  const [direction, setDirection] = useState<"ASC" | "DESC" | "">("ASC");

  const [selected, setSelected] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
    const savedPageSize = localStorage.getItem("ManualL2itemsPerPage");
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });

  const key = useMemo(
    () => columns.find((c) => c.key)?.fieldName || "id",
    [columns]
  );
  const memoizedColumns = useMemo(() => columns, [columns]);

  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size);
    localStorage.setItem("ManualL2itemsPerPage", size.toString());
    pageSizeChanged && pageSizeChanged(size);
  };

  const updateSort = (col: IGridcolumn) => () => {
    if (col.sortable && sortChanged) {
      if (sort === col.fieldName) {
        sortChanged(col.fieldName, direction === "ASC" ? "DESC" : "ASC");
      } else {
        sortChanged(col.fieldName, "ASC");
      }
      setSort(col.fieldName);
    }
  };

  const checkAllTestResult = () => {
    if (!allSelected) {
      const allResultId = data.map((result: any) => result[key]);
      setSelected(allResultId);
    } else {
      setSelected([]);
    }
  };

  const handleCheckboxChange = useCallback((id: string, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  }, []);

  const onPageChanged = (e: any, page: number) => {
    localStorage.setItem("ManualL2currentPage", page.toString());
    pageChanged && pageChanged(page);
  };

  useEffect(() => {
    setOrder(sortBy.substring(1));
    setDirection(sortBy.charAt(0) === "-" ? "DESC" : "ASC");
  }, [sortBy, sortDirection]);

  useEffect(() => {
    setAllSelected(selected.length === data.length && data.length > 0);
    selectionChanged && selectionChanged(selected);
  }, [selected, selectionChanged, data.length]);

  return (
    <Table>
      <TableHead>
        <TableRow>
          {selection && (
            <TableCell width={"3%"}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  color="primary"
                  onChange={checkAllTestResult}
                  checked={allSelected}
                />
                <span style={{ marginLeft: 4 }}>Select</span>
              </div>
            </TableCell>
          )}
          {memoizedColumns.map((col, colIndex) => (
            <TableCell
              key={`col_${colIndex}_${col.fieldName ?? col.caption ?? "col"}`}
              width={col.width || ""}
              style={{
                textAlign: col.caption === "ACTION" ? "center" : "left"
              }}
            >
              <div
                onClick={updateSort(col)}
                className={`${col.caption === "ACTION" ? "" : "flex-center"} ${
                  col.sortable ? "pointer" : ""
                }`}
              >
                {col.caption}
                {col.fieldName === order &&
                  (direction === "ASC" ? (
                    <ArrowDropUpOutlinedIcon />
                  ) : (
                    <ArrowDropDownOutlinedIcon />
                  ))}
              </div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {data.map((row, index) => (
          <TableDataRow
            key={`row_${index}`}
            row={row}
            columns={memoizedColumns}
            isSelected={selected.includes(row[key])}
            selection={selection}
            onCheckboxChange={handleCheckboxChange}
            rowKey={key}
          />
        ))}
        {data.length < 10 &&
          Array.from({ length: 10 - data.length }).map((_, i) => (
            <TableRow key={`empty_${i}`}>
              <TableCell colSpan={columns.length + (selection ? 1 : 0)} />
            </TableRow>
          ))}
      </TableBody>

      {totalPages >= 0 && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={columns.length + (selection ? 1 : 0)}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <Pagination
                  onChange={onPageChanged}
                  count={totalPages}
                  page={currentPage}
                  showFirstButton
                  showLastButton
                />
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <PaginationGoToPageTextbox
                    totalPages={totalPages}
                    currentPage={currentPage}
                    hadlePageChange={(page: number) =>
                      onPageChanged(null, page)
                    }
                  />
                  <PageSizeSelector
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    itemsPerPage={itemsPerPage}
                  />
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
};
